import { getIntakeImportRepeaterPath, isImportableIntakePatchPath, isImportableIntakeScalarPath, isSensitiveIntakeImportPath, type IntakeImportPatch } from "../../../shared/intake-import";
import { createDoctorProfile, createProcedureDetail, createProductOption } from "../form-schema";
import type { IntakeData, IntakeStep } from "../types";
import { fieldValueToString, getPathValue, setPathValue } from "./value-utils";

export type IntakeImportApplyResult = {
  data: IntakeData;
  applied: IntakeImportPatch[];
  skipped: IntakeImportPatch[];
};

export type IntakeImportContext = {
  draftId: string;
  categoryId: string;
  schemaHash: string;
};

export function isSameIntakeImportContext(left: IntakeImportContext, right: IntakeImportContext): boolean {
  return left.draftId === right.draftId && left.categoryId === right.categoryId && left.schemaHash === right.schemaHash;
}

export function isCurrentIntakeImportRequest(requestId: number, activeRequestId: number, requestContext: IntakeImportContext, currentContext: IntakeImportContext): boolean {
  return requestId === activeRequestId && isSameIntakeImportContext(requestContext, currentContext);
}

export function shouldShowAdminIntakeImport(pathname: string, isCategoryFlow: boolean): boolean {
  return pathname !== "/" && isCategoryFlow;
}

export function shouldClearAdminIntakeImportNoticeOnSchemaChange(hasPendingProposal: boolean): boolean {
  return hasPendingProposal;
}

type IntakeImportApplyInput = {
  data: IntakeData;
  baselineData: IntakeData;
  patches: IntakeImportPatch[];
  overwriteExisting: boolean;
};

export function applyIntakeImportPatches({ data, baselineData, patches, overwriteExisting }: IntakeImportApplyInput): IntakeImportApplyResult {
  let nextData = data;
  const applied: IntakeImportPatch[] = [];
  const skipped: IntakeImportPatch[] = [];
  const appliedPaths = new Set<string>();
  const seenPaths = new Set<string>();

  for (const patch of patches) {
    const path = patch.path.trim();
    const value = patch.value.trim();
    if (!path || !value || seenPaths.has(path) || !isImportableIntakePatchPath(path) || isSensitiveIntakeImportPath(path)) {
      skipped.push(patch);
      continue;
    }
    seenPaths.add(path);

    if (isImportableIntakeScalarPath(path)) {
      const currentValue = fieldValueToString(getPathValue(nextData, path));
      const baselineValue = fieldValueToString(getPathValue(baselineData, path));
      if (!canReplaceValue(currentValue, baselineValue, overwriteExisting)) {
        skipped.push(patch);
        continue;
      }
      nextData = setPathValue(nextData, path, value);
      applied.push({ ...patch, path, value });
      appliedPaths.add(path);
      continue;
    }

    const repeater = getIntakeImportRepeaterPath(path);
    if (!repeater) {
      skipped.push(patch);
      continue;
    }
    const result = applyRepeaterPatch(nextData, baselineData, repeater, value, overwriteExisting);
    if (!result.applied) {
      skipped.push(patch);
      continue;
    }
    nextData = result.data;
    applied.push({ ...patch, path, value });
    appliedPaths.add(path);
  }

  return { data: reconcileDependentContent(nextData, appliedPaths), applied, skipped };
}

export function describeIntakeImportPatch(patch: IntakeImportPatch, steps: IntakeStep[]): string {
  const scalarField = steps.flatMap((step) => step.fields ?? []).find((field) => field.path === patch.path);
  if (scalarField) return scalarField.label;

  const repeaterPath = getIntakeImportRepeaterPath(patch.path);
  if (!repeaterPath) return patch.path;
  for (const step of steps) {
    const repeater = step.repeaters?.find((candidate) => candidate.path === repeaterPath.repeaterPath);
    const field = repeater?.fields.find((candidate) => candidate.path === repeaterPath.fieldPath);
    if (repeater && field) return `${repeater.label} ${repeaterPath.index + 1} · ${field.label}`;
  }
  return patch.path;
}

function applyRepeaterPatch(
  data: IntakeData,
  baselineData: IntakeData,
  repeater: NonNullable<ReturnType<typeof getIntakeImportRepeaterPath>>,
  value: string,
  overwriteExisting: boolean,
): { data: IntakeData; applied: boolean } {
  const currentItems = getRecordArray(getPathValue(data, repeater.repeaterPath));
  const baselineItems = getRecordArray(getPathValue(baselineData, repeater.repeaterPath));
  const nextItems = [...currentItems];
  while (nextItems.length <= repeater.index) nextItems.push(createRepeaterItem(repeater.repeaterPath));

  const item = getRecord(nextItems[repeater.index]);
  const baselineItem = getRecord(baselineItems[repeater.index]);
  const currentValue = fieldValueToString(item[repeater.fieldPath]);
  const baselineValue = fieldValueToString(baselineItem[repeater.fieldPath]);
  if (!canReplaceValue(currentValue, baselineValue, overwriteExisting)) return { data, applied: false };

  nextItems[repeater.index] = { ...item, [repeater.fieldPath]: value };
  return { data: setPathValue(data, repeater.repeaterPath, nextItems), applied: true };
}

function reconcileDependentContent(data: IntakeData, appliedPaths: Set<string>): IntakeData {
  let nextData = data;
  if (appliedPaths.has("content.sellingPointTags") && !splitMultiValue(nextData.content.sellingPointTags).includes("관광객 방문 편의")) {
    nextData = setPathValue(nextData, "content.touristConvenienceOptions", "");
  }
  if (appliedPaths.has("content.targetCustomers") && !splitMultiValue(nextData.content.targetCustomers).includes("특정 고민이 있는 고객")) {
    nextData = setPathValue(nextData, "content.targetCustomerConcernDetail", "");
  }
  if (appliedPaths.has("content.creatripExclusiveBenefitStatus") && nextData.content.creatripExclusiveBenefitStatus !== "있음") {
    nextData = setPathValue(nextData, "content.creatripExclusiveBenefit", "");
  }
  return nextData;
}

function canReplaceValue(currentValue: string, baselineValue: string, overwriteExisting: boolean): boolean {
  return overwriteExisting || !currentValue.trim() || currentValue === baselineValue;
}

function createRepeaterItem(repeaterPath: string): Record<string, unknown> {
  if (repeaterPath === "products") return createProductOption();
  if (repeaterPath === "doctors") return createDoctorProfile();
  if (repeaterPath === "procedures") return createProcedureDetail();
  return {};
}

function getRecordArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map((item) => getRecord(item)) : [];
}

function getRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function splitMultiValue(value: string): string[] {
  return value.split(/[\n,]/).map((item) => item.trim()).filter(Boolean);
}
